import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import Card from './Card';
import MetricCard from './MetricCard';
import InfoPopover from './common/InfoPopover';
import { AllKpiData } from '../hooks/useKommunData';
import { kpiMeta } from '../config/kpi-config';
import KpiSkeleton from './feedback/KpiSkeleton';
import { useFavorites } from '../context/FavoritesContext';
import { useIsDesktop } from '../hooks/useIsDesktop';

type Group = 'ak9' | 'ak6' | 'resources';

interface KpiGroupPanelProps {
  group: Group;
  kpiData: AllKpiData;
  latestYear: number;
  activeKpi: string;
  onKpiSelect: (code: string) => void;
  compact?: boolean; // New prop for compact row layout
  className?: string; // New prop for custom styling
  loading?: boolean; // Loading state
}

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const KpiGroupPanel: React.FC<KpiGroupPanelProps> = ({
  group,
  kpiData,
  latestYear,
  activeKpi,
  onKpiSelect,
  compact = false,
  className = "",
  loading = false
}) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const isDesktop = useIsDesktop();

  const TITLE: Record<Group, string> = {
    ak9: "Resultat Åk 9",
    ak6: "Resultat Åk 6",
    resources: "Resurser & Struktur",
  };

  const HELP: Record<Group, string> = {
    ak9: "Δ visar förändring mot föregående år (p.p./poäng). p.p. = procentenheter.",
    ak6: "Δ visar förändring mot föregående år (p.p.). p.p. = procentenheter.",
    resources: "Δ visar förändring mot föregående år (p.p./st/HT). p.p. = procentenheter.",
  };

  // Filter KPIs by group and sort by: 1. Favorites, 2. Config order
  const groupKpis = useMemo(() => {
    const codes = Object.keys(kpiData).filter(code => {
      const meta = kpiMeta[code];
      return meta && meta.group === group && kpiData[code] && kpiData[code].length > 0;
    });

    return codes.sort((a, b) => {
      // 1. Sort Favorites to top
      const favA = isFavorite(a);
      const favB = isFavorite(b);
      if (favA && !favB) return -1;
      if (!favA && favB) return 1;

      // 2. Sort by configured sort order
      const metaA = kpiMeta[a];
      const metaB = kpiMeta[b];
      const sortOrderA = metaA?.sortOrder ?? 999;
      const sortOrderB = metaB?.sortOrder ?? 999;

      return sortOrderA - sortOrderB;
    });
  }, [kpiData, group, isFavorite]);

  // Always display all KPIs - no progressive disclosure
  const displayKpis = groupKpis;

  if (groupKpis.length === 0) {
    return (
      <Card>
        <div className="p-4 text-center text-slate-500 dark:text-slate-400">
          Ingen data tillgänglig för {TITLE[group].toLowerCase()}
        </div>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {/* Section header */}
      <div className="p-3 pb-2 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {TITLE[group]}
            </h3>
            <InfoPopover
              content={HELP[group]}
              title="Förklaring"
              placement="bottom"
            />
          </div>
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            Senaste data: {latestYear}
          </span>
        </div>
      </div>

      {/* KPI cards - responsive grid layout */}
      <div className={isDesktop ? "p-3" : "p-4"} role="listbox">
        {loading ? (
          <KpiSkeleton count={displayKpis.length} compact={compact} />
        ) : (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className={
              isDesktop
                ? "grid gap-4 grid-cols-3"
                : "grid gap-2 grid-cols-2"
            }
          >
            {displayKpis.map(code => {
              const data = kpiData[code];
              const latestEntry = data[data.length - 1];
              const latestValue = latestEntry?.value;
              const prevYearValue = latestEntry?.prevYearValue;
              const isPinned = isFavorite(code);

              return (
                <motion.div
                  key={code}
                  variants={item}
                  className="relative group h-full"
                >
                  <MetricCard
                    kpiId={code}
                    latestValue={latestValue}
                    prevYearValue={prevYearValue}
                    timeseries={data}
                    isActive={activeKpi === code}
                    onClick={() => onKpiSelect(code)}
                    compact={compact}
                    className="h-full"
                  />

                  {/* Pin Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(code);
                    }}
                    className={`
                      absolute top-2 right-2 p-1.5 rounded-full z-10 transition-all duration-200
                      ${isPinned
                        ? 'bg-red-50 text-red-500 dark:bg-red-900/30'
                        : 'bg-white/50 dark:bg-slate-800/50 text-slate-400 hover:text-red-500 opacity-60 hover:opacity-100'
                      }
                    `}
                    aria-label={isPinned ? "Ta bort favorit" : "Lägg till favorit"}
                  >
                    <Heart
                      className={`w-4 h-4 transition-transform ${isPinned ? 'fill-current scale-110' : 'scale-100'}`}
                    />
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>

    </Card>
  );
};

export default KpiGroupPanel;
